import React, { useMemo, useState } from 'react';
import {
  Building,
  Users,
  ClipboardList,
  TrendingUp,
  Gift,
  ChevronLeft,
  ChevronRight,
  Menu,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

/* ===================== Animaciones ===================== */
const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const gridVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

/* ===================== Tarjeta de Métrica ===================== */
interface MetricCardProps {
  titulo: string;
  valor: number | undefined;
  icono: React.ReactNode;
  delta?: number | null;
  textoComparacion?: string;
  className?: string;
  esDinero?: boolean;
}

const TarjetaMetrica: React.FC<MetricCardProps> = ({
  titulo,
  valor,
  icono,
  delta,
  textoComparacion,
  className = '',
  esDinero = false,
}) => {
  const safeValue = valor ?? 0;

  const renderValor = () => {
    if (esDinero)
      return `${safeValue.toLocaleString('es-ES', {
        style: 'currency',
        currency: 'EUR',
      })}`;
    return safeValue.toLocaleString('es-ES');
  };

  const mostrarDelta =
    delta !== undefined &&
    delta !== null &&
    textoComparacion === 'vs mes pasado';

  return (
    <motion.div
      whileHover={{ scale: 1.012 }}
      transition={{ duration: 0.15 }}
      className="h-full"
    >
      <Card
        className={`rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-150 h-full ${className}`}
      >
        <CardContent className="p-4 flex flex-col h-full justify-between gap-3">
          {/* Título + icono */}
          <div className="flex justify-between items-center">
            <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
              {titulo}
            </h3>
            <div className="w-10 h-10 flex items-center justify-center rounded-md bg-[#103254] text-white shadow-md shadow-[#103254]/40">
              <div className="w-5 h-5 flex items-center justify-center">
                {icono}
              </div>
            </div>
          </div>

          {/* Valor */}
          <div className="flex-1 flex items-end">
            <p className="text-3xl font-bold text-gray-900 dark:text-white leading-none select-none">
              {renderValor()}
            </p>
          </div>

          {/* Delta */}
          <div className="min-h-[22px]">
            {mostrarDelta && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-sm font-semibold ${
                      delta > 0
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}
                  >
                    {delta > 0
                      ? `+${delta.toFixed(2)}%`
                      : `${delta.toFixed(2)}%`}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {textoComparacion}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

/* ===================== Dashboard Admin ===================== */
const DashboardAdmin: React.FC = () => {
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({
    usuarios: true,
    suscripciones: true,
    agendas: true,
    ingresos: true,
  });

  const ahora = new Date();
  const [mes, setMes] = useState(ahora.getMonth() + 1);
  const [anio, setAnio] = useState(ahora.getFullYear());

  const meses = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  const mesAnterior = () =>
    setMes((m) => {
      if (m === 1) {
        setAnio((a) => a - 1);
        return 12;
      }
      return m - 1;
    });

  const mesSiguiente = () =>
    setMes((m) => {
      if (m === 12) {
        setAnio((a) => a + 1);
        return 1;
      }
      return m + 1;
    });

  const obtenerMesesVisibles = () => {
    const visibles = [];
    for (let o = -2; o <= 2; o++) visibles.push((mes - 1 + o + 12) % 12);
    return visibles;
  };

  /* ✅ DATOS MOCK */
  const datos = {
    totalEmpresas: 312,
    nuevasEmpresasMes: 18,
    totalUsuarios: 9850,
    totalTrabajadores: 1740,
    totalSuscripciones: 3720,
    suscripcionesActivas: 3100,
    suscripcionesCanceladas: 620,
    totalAgendas: 1420,
    agendasActivas: 1160,
    agendasCanceladas: 260,
    ingresosSuscripciones: 18250,
    ingresosAgendas: 8150,
    variaciones: {
      empresasMes: 4,
      usuariosMes: 3,
      trabajadoresMes: 5,
      suscripcionesMes: 4,
      suscripcionesActivasMes: 5,
      suscripcionesCanceladasMes: -2,
      agendasMes: 3,
      agendasActivasMes: 4,
      agendasCanceladasMes: -1,
      ingresosTotalesMes: 6,
      ingresosSuscripcionesMes: 5,
      ingresosAgendasMes: 3,
    },
  };

  const ingresosTotales = useMemo(
    () => datos.ingresosSuscripciones + datos.ingresosAgendas,
    [datos.ingresosSuscripciones, datos.ingresosAgendas]
  );

  /* Charts Mock */
  const altasPorSemana = [
    { semana: 'S-40', altas: 18 },
    { semana: 'S-41', altas: 22 },
    { semana: 'S-42', altas: 14 },
    { semana: 'S-43', altas: 19 },
    { semana: 'S-44', altas: 17 },
  ];
  const agendasPorMes = meses.map((m, i) => ({
    mes: m.slice(0, 3),
    valor: 90 + i * 2,
  }));
  const ingresosMensuales = agendasPorMes.map((e) => ({
    mes: e.mes,
    valor: +(e.valor * 200).toFixed(2),
  }));
  const ultimasEmpresas = [
    { nombre: 'Belleza Top', fecha: '22/10/2025', estado: 'Activa' },
    { nombre: 'Spa Relax', fecha: '21/10/2025', estado: 'Activa' },
    { nombre: 'Barber King', fecha: '20/10/2025', estado: 'Activa' },
    { nombre: 'Derm&Skin', fecha: '19/10/2025', estado: 'Pendiente' },
  ];

  const metricas = [
    {
      categoria: 'usuarios',
      titulo: 'Total empresas',
      valor: datos.totalEmpresas,
      icono: <Building className="w-5 h-5" />,
      delta: datos.variaciones.empresasMes,
      textoComparacion: 'vs mes pasado',
    },
    {
      categoria: 'usuarios',
      titulo: 'Registradas mes',
      valor: datos.nuevasEmpresasMes,
      icono: <ClipboardList className="w-5 h-5" />,
      delta: datos.variaciones.empresasMes,
      textoComparacion: 'vs mes pasado',
    },
    {
      categoria: 'usuarios',
      titulo: 'Total usuarios',
      valor: datos.totalUsuarios,
      icono: <Users className="w-5 h-5" />,
      delta: datos.variaciones.usuariosMes,
      textoComparacion: 'vs mes pasado',
    },
    {
      categoria: 'usuarios',
      titulo: 'Usuarios mes',
      valor: datos.totalUsuarios,
      icono: <Users className="w-5 h-5" />,
      delta: datos.variaciones.usuariosMes,
      textoComparacion: 'vs mes pasado',
    },
    {
      categoria: 'suscripciones',
      titulo: 'Total contratadas',
      valor: datos.totalSuscripciones,
      icono: <Gift className="w-5 h-5" />,
      delta: datos.variaciones.suscripcionesMes,
      textoComparacion: 'vs mes pasado',
    },
    {
      categoria: 'suscripciones',
      titulo: 'Contratadas mes',
      valor: datos.suscripcionesActivas,
      icono: <TrendingUp className="w-5 h-5" />,
      delta: datos.variaciones.suscripcionesActivasMes,
      textoComparacion: 'vs mes pasado',
    },
    {
      categoria: 'suscripciones',
      titulo: 'Total canceladas',
      valor: datos.suscripcionesCanceladas,
      icono: <TrendingUp className="w-5 h-5" />,
      delta: datos.variaciones.suscripcionesCanceladasMes,
      textoComparacion: 'vs mes pasado',
    },
    {
      categoria: 'suscripciones',
      titulo: 'Canceladas mes',
      valor: datos.suscripcionesCanceladas,
      icono: <TrendingUp className="w-5 h-5" />,
      delta: datos.variaciones.suscripcionesCanceladasMes,
      textoComparacion: 'vs mes pasado',
    },
    {
      categoria: 'agendas',
      titulo: 'Total contratadas',
      valor: datos.totalAgendas,
      icono: <ClipboardList className="w-5 h-5" />,
      delta: datos.variaciones.agendasMes,
      textoComparacion: 'vs mes pasado',
    },
    {
      categoria: 'agendas',
      titulo: 'Contratadas mes',
      valor: datos.agendasActivas,
      icono: <TrendingUp className="w-5 h-5" />,
      delta: datos.variaciones.agendasActivasMes,
      textoComparacion: 'vs mes pasado',
    },
    {
      categoria: 'agendas',
      titulo: 'Total canceladas',
      valor: datos.agendasCanceladas,
      icono: <TrendingUp className="w-5 h-5" />,
      delta: datos.variaciones.agendasCanceladasMes,
      textoComparacion: 'vs mes pasado',
    },
    {
      categoria: 'agendas',
      titulo: 'Canceladas mes',
      valor: datos.agendasCanceladas,
      icono: <TrendingUp className="w-5 h-5" />,
      delta: datos.variaciones.agendasCanceladasMes,
      textoComparacion: 'vs mes pasado',
    },
    {
      categoria: 'ingresos',
      titulo: 'Total ingresos',
      valor: ingresosTotales,
      icono: <Gift className="w-5 h-5" />,
      delta: datos.variaciones.ingresosTotalesMes,
      textoComparacion: 'vs mes pasado',
      esDinero: true,
    },
    {
      categoria: 'ingresos',
      titulo: 'Ingresos mes',
      valor: ingresosTotales,
      icono: <Gift className="w-5 h-5" />,
      delta: datos.variaciones.ingresosTotalesMes,
      textoComparacion: 'vs mes pasado',
      esDinero: true,
    },
    {
      categoria: 'ingresos',
      titulo: 'Ingresos suscripciones',
      valor: datos.ingresosSuscripciones,
      icono: <TrendingUp className="w-5 h-5" />,
      delta: datos.variaciones.ingresosSuscripcionesMes,
      textoComparacion: 'vs mes pasado',
      esDinero: true,
    },
    {
      categoria: 'ingresos',
      titulo: 'Ingresos agendas',
      valor: datos.ingresosAgendas,
      icono: <TrendingUp className="w-5 h-5" />,
      delta: datos.variaciones.ingresosAgendasMes,
      textoComparacion: 'vs mes pasado',
      esDinero: true,
    },
  ];

  const axisStyle = { fill: '#6b7280', fontSize: 10 };
  const tooltipStyle = {
    background: '#ffffff',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
    color: '#103254',
    fontSize: 12,
  };

  return (
    <motion.div
      className="space-y-8"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* ── Cabecera ── */}
      <motion.div variants={itemVariants} className="w-full">
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Menu className="w-4 h-4 mr-2 text-gray-500" />
          <span className="mr-2">Dashboard</span>
          <span className="mx-1 text-gray-600">/</span>
          <h1 className="font-semibold text-gray-900 dark:text-white">
            Admin — {meses[mes - 1]} {anio}
          </h1>
        </div>
      </motion.div>

      {/* ── Selector de meses ── */}
      <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
        <button
          onClick={mesAnterior}
          className="hidden sm:flex p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {obtenerMesesVisibles().map((idx) => {
          const nombre =
            meses[idx].charAt(0).toUpperCase() + meses[idx].slice(1);
          return (
            <button
              key={idx}
              onClick={() => setMes(idx + 1)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex-shrink-0 ${
                mes === idx + 1
                  ? 'bg-[#103254] text-white shadow-lg shadow-[#103254]/40 scale-105'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
              }`}
            >
              <span className="sm:hidden">{nombre.slice(0, 3)}</span>
              <span className="hidden sm:inline">{nombre}</span>
            </button>
          );
        })}

        <button
          onClick={mesSiguiente}
          className="hidden sm:flex p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* ── Secciones colapsables ── */}
      {[
        { titulo: 'Usuarios y Empresas', categoria: 'usuarios' },
        { titulo: 'Suscripciones', categoria: 'suscripciones' },
        { titulo: 'Agendas', categoria: 'agendas' },
        { titulo: 'Ingresos', categoria: 'ingresos' },
      ].map((sec, index) => {
        const isCollapsed = collapsedSections[sec.categoria];

        return (
          <Card
            key={index}
            className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm"
          >
            <CardContent className="p-6 space-y-4">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() =>
                  setCollapsedSections((prev) => ({
                    ...prev,
                    [sec.categoria]: !prev[sec.categoria],
                  }))
                }
              >
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  {sec.titulo}
                </h2>
                <motion.div
                  animate={{ rotate: isCollapsed ? -90 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </motion.div>
              </div>

              <motion.div
                initial={false}
                animate={{
                  height: isCollapsed ? 0 : 'auto',
                  opacity: isCollapsed ? 0 : 1,
                }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <motion.div
                  variants={gridVariants}
                  className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-2"
                >
                  {!isCollapsed &&
                    metricas
                      .filter((m) => m.categoria === sec.categoria)
                      .map((m, i) => <TarjetaMetrica key={i} {...m} />)}
                </motion.div>
              </motion.div>
            </CardContent>
          </Card>
        );
      })}

      {/* ── Gráficas ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <Card className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 pb-4">
              Altas empresas (semana)
            </h2>
            <div className="w-full h-48">
              <ResponsiveContainer>
                <BarChart data={altasPorSemana}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" />
                  <XAxis dataKey="semana" tick={axisStyle} />
                  <YAxis tick={axisStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="altas" fill="#103254" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 pb-4">
              Agendas / Trabajadores
            </h2>
            <div className="w-full h-48">
              <ResponsiveContainer>
                <BarChart data={agendasPorMes}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" />
                  <XAxis dataKey="mes" tick={axisStyle} />
                  <YAxis tick={axisStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="valor" fill="#103254" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 pb-4">
              Ingresos totales
            </h2>
            <div className="w-full h-48">
              <ResponsiveContainer>
                <LineChart data={ingresosMensuales}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" />
                  <XAxis dataKey="mes" tick={axisStyle} />
                  <YAxis tick={axisStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line
                    type="monotone"
                    dataKey="valor"
                    stroke="#103254"
                    strokeWidth={2.5}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Últimas empresas ── */}
      <Card className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
        <CardContent className="p-6">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 pb-4">
            Últimas empresas registradas
          </h2>

          <div className="space-y-1">
            {ultimasEmpresas.map((e, i) => (
              <div
                key={i}
                className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#103254]/8 dark:bg-[#103254]/40 rounded-md flex items-center justify-center">
                    <Building className="w-4 h-4 text-[#103254] dark:text-blue-300" />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 dark:text-gray-200">
                      {e.nombre}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {e.fecha}
                    </p>
                  </div>
                </div>
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-md ${
                    e.estado === 'Activa'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}
                >
                  {e.estado}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default DashboardAdmin;